import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAtom, useAtomValue } from 'jotai/react'

import { Github } from '@/components/svgr/contact/complete'
import { LoginStatus } from '@/constants/common'
import { DEFAULT_AVATAR } from '@/constants/wallet'
import { GithubAuthState } from '@/hooks/services/useGithubAuth'
import { TwitterAuthState } from '@/hooks/services/useTwitterAuth'
import useMediaQuery from '@/hooks/useMediaQuery'
import { useWallet } from '@/hooks/useWallet'
import { formatAddress } from '@/lib/format'
import useSolana from '@/lib/solana/hooks/useSolana'
import { cn } from '@/lib/utils'
import { useGlobalMethodProviderContext } from '@/providers/GlobalMethodProvider'
import { RoutePath } from '@/routes'
import {
  githubAuthStateAtom,
  loginStatusAtom,
  twitterAuthStateAtom,
  userInfoAtom
} from '@/stores/services'

import { Grid } from './common/Box'
import { BeautyButton, Button } from './common/Button'
import { ProfileDialog } from './common/Dialog'
import DropdownMenu from './common/DropdownMenu'
import { Text } from './common/Text'
import { Exit, Wallet, X } from './svgr/common'
import { MyToken } from './svgr'

const ConnectWallet: React.FC<{
  className?: string
  handleCloseDrawer?: () => void
}> = ({ className }) => {
  const { gtXL } = useMediaQuery()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { connect, disconnect } = useWallet()
  const { address, connecting, getBalance } = useSolana()
  const {
    loginLoading,
    signMessageWithLogin,
    // openTwitterAuth,
    // openGithubAuth,
    authTwitter,
    authGithub
  } = useGlobalMethodProviderContext() || {}

  const twitterAuthState = useAtomValue(twitterAuthStateAtom)
  const githubAuthState = useAtomValue(githubAuthStateAtom)
  const loginStatus = useAtomValue(loginStatusAtom)
  const userInfo = useAtomValue(userInfoAtom)

  useEffect(() => {
    getBalance()
  }, [getBalance])

  if (loginStatus === LoginStatus.LOGIN && address)
    return (
      <DropdownMenu
        content={{
          className: 'p-2 xl:px-2 xl:py-2 w-auto mx-0',
          align: 'end'
        }}
        trigger={{
          asChild: true,
          children: (
            <BeautyButton beauty={false} selector role="account-menu">
              <img
                src={userInfo?.photo || DEFAULT_AVATAR}
                className="size-4 rounded-full"
              />
              <Text
                className={cn(
                  'hidden xl:text-base',
                  'text-xs leading-none text-white xl:block'
                )}
              >
                {userInfo?.uname || formatAddress(address, 2, -4)}
              </Text>
            </BeautyButton>
          )
        }}
      >
        <Grid className="gap-2 [&_button]:justify-start [&_button]:shadow-none [&_button]:xl:h-10 [&_button]:xl:px-2 [&_button]:xl:text-base">
          <ProfileDialog />
          <Button size="sm" ghost onClick={() => navigate(RoutePath.MY_COIN)}>
            <MyToken className="text-color-text-primary" />
            <Text className="font-light">{t('coin.create.myTokens')}</Text>
          </Button>
          {twitterAuthState !== TwitterAuthState.UN_KNOWN && (
            <Button
              size="sm"
              ghost
              onClick={() => {
                if (twitterAuthState === TwitterAuthState.AUTHENTICATED) return
                authTwitter()
                // setTwitterAuthState(TwitterAuthState.UNAUTHENTICATED)
                // openTwitterAuth?.()
              }}
            >
              <X className="text-color-text-primary" />
              <Text className="font-light">
                {t(
                  twitterAuthState === TwitterAuthState.AUTHENTICATED
                    ? 'twitter.linked'
                    : 'twitter.link'
                )}
              </Text>
            </Button>
          )}
          {githubAuthState !== GithubAuthState.UN_KNOWN && (
            <Button
              size="sm"
              ghost
              onClick={() => {
                if (githubAuthState === GithubAuthState.AUTHENTICATED) return
                authGithub()
                // setGithubAuthState(GithubAuthState.UNAUTHENTICATED)
                // openGithubAuth?.()
              }}
            >
              <Github className="text-color-text-primary" />
              <Text className="font-light">
                {t(
                  githubAuthState === GithubAuthState.AUTHENTICATED
                    ? 'auth.github.linked'
                    : 'auth.github.link'
                )}
              </Text>
            </Button>
          )}
          <Button role="disconnect-wallet" size="sm" ghost onClick={disconnect}>
            <Exit className="text-color-text-primary" />
            <Text className="font-light">{t('wallet.disconnect')}</Text>
          </Button>
        </Grid>
      </DropdownMenu>
    )

  if (
    (loginStatus === LoginStatus.NOTOKEN ||
      loginStatus === LoginStatus.UNSIGNED) &&
    address
  )
    return (
      <BeautyButton
        role="sign-wallet"
        data-status={loginStatus}
        beauty={false}
        loading={loginLoading}
        disabled={loginLoading}
        className={cn(
          'text-xs leading-none text-white xl:text-base',
          className
        )}
        onClick={signMessageWithLogin}
      >
        {t('wallet.sign')}
      </BeautyButton>
    )

  return (
    <BeautyButton
      role="connect-wallet"
      className={cn('text-xs leading-none text-white xl:text-base', className)}
      onClick={connect}
      loading={connecting}
      disabled={connecting}
      beauty={false}
    >
      {gtXL ? (
        connecting ? (
          'Connecting...'
        ) : (
          t('wallet.connect')
        )
      ) : (
        <Wallet className="text-white" />
      )}
    </BeautyButton>
  )
}

export default ConnectWallet
